// Property Ownership Module — Phase 2 UI
//
// One <PropertiesManager /> component; each property renders as a
// progressive-disclosure <PropertyCard />. The default view shows the
// summary numbers; users open the card to reveal financing, expenses,
// and rental details.
import { useEffect, useMemo, useState } from "react";
import {
  Home, Plus, Trash2, Copy, ChevronDown, ChevronUp, AlertTriangle,
  Link as LinkIcon, Percent, DollarSign, TrendingUp, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { COUNTRIES, CURRENCIES } from "@/lib/countries";

const PROPERTY_TYPES = [
  ["primary_residence", "Primary residence"],
  ["condo",             "Condo"],
  ["townhouse",         "Townhouse"],
  ["detached",          "Detached house"],
  ["semi_detached",     "Semi-detached"],
  ["rental",            "Rental property"],
  ["vacation",          "Vacation property"],
  ["commercial",        "Commercial property"],
  ["land",              "Land"],
  ["mixed_use",         "Mixed-use"],
  ["other",             "Other"],
];

const FINANCING_TYPES = [
  ["mortgage",          "Mortgage"],
  ["heloc",             "HELOC"],
  ["second_mortgage",   "Second mortgage"],
  ["vendor_financing",  "Vendor financing"],
  ["private_loan",      "Private loan"],
  ["construction",      "Construction loan"],
  ["improvement_loan",  "Home improvement"],
  ["other_secured",     "Other secured"],
];

const PAYMENT_FREQ = [
  ["weekly",              "Weekly"],
  ["accelerated_weekly",  "Accelerated weekly"],
  ["biweekly",            "Biweekly"],
  ["accelerated_biweekly","Accelerated biweekly"],
  ["semi_monthly",        "Semi-monthly"],
  ["monthly",             "Monthly"],
  ["quarterly",           "Quarterly"],
  ["annual",              "Annual"],
];

const EXPENSE_KINDS = [
  ["property_tax",   "Property tax"],
  ["condo_fees",     "Condo fees"],
  ["hoa",            "HOA fees"],
  ["insurance",      "Home insurance"],
  ["utilities",      "Utilities"],
  ["maintenance",    "Maintenance"],
  ["repairs",        "Repairs"],
  ["management",     "Property management"],
  ["vacancy_allowance","Vacancy allowance"],
  ["mortgage_insurance","Mortgage insurance (CMHC etc.)"],
  ["ownership_fees", "Land transfer / registration"],
  ["other",          "Other"],
];

export default function PropertiesManager() {
  const [state, setState] = useState({ loading: true, error: null, properties: [], portfolio: null, notes: [], meta: null });
  const [showAdd, setShowAdd] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/financial-plan/properties");
      setState({
        loading: false, error: null,
        properties: data.properties || [],
        portfolio: data.portfolio || null,
        notes: data.notes || [],
        meta: { currency: data.currency, locale: data.locale },
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || e.message }));
    }
  };

  const loadSuggestions = async () => {
    try {
      const { data } = await api.get("/financial-plan/properties/reconcile/suggest-links");
      setSuggestions(data.suggestions || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    loadSuggestions();
  }, []);

  const create = async (body) => {
    try {
      await api.post("/financial-plan/properties", body);
      toast.success("Property added");
      setShowAdd(false);
      await load();
      loadSuggestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add property");
    }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/financial-plan/properties/${id}`, patch);
      await load();
      loadSuggestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/financial-plan/properties/${id}`);
      toast.success("Property archived");
      await load();
    } catch (e) {
      toast.error("Could not archive");
    }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/financial-plan/properties/${id}/duplicate`);
      toast.success("Duplicated");
      await load();
    } catch (e) {
      toast.error("Could not duplicate");
    }
  };

  const confirmLink = async (s) => {
    try {
      await api.post("/financial-plan/properties/link-debt", {
        property_id: s.property_id, financing_id: s.financing_id, debt_id: s.debt_id,
      });
      toast.success("Mortgage linked — it will now appear once in your liabilities");
      await load();
      loadSuggestions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link");
    }
  };

  if (state.loading) return <div className="text-sm text-muted-foreground py-4" data-testid="properties-loading">Loading properties…</div>;
  if (state.error) return <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-500 flex gap-2" data-testid="properties-error"><AlertTriangle className="w-4 h-4" /> {state.error}</div>;

  const isEmpty = !state.properties.length;
  return (
    <div className="space-y-4" data-testid="properties-manager">
      <PortfolioSummary portfolio={state.portfolio} currency={state.meta?.currency} />

      {suggestions.length > 0 && (
        <SuggestionsPanel suggestions={suggestions} onConfirm={confirmLink} currency={state.meta?.currency} />
      )}

      {isEmpty ? (
        <EmptyState onAdd={() => setShowAdd(true)} />
      ) : (
        <div className="space-y-3">
          {state.properties.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              currency={state.meta?.currency}
              onUpdate={(patch) => update(p.id, patch)}
              onDelete={() => remove(p.id)}
              onDuplicate={() => duplicate(p.id)}
            />
          ))}
          <button
            onClick={() => setShowAdd(true)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="properties-add-btn"
          >
            <Plus className="w-4 h-4" /> Add another property
          </button>
        </div>
      )}

      {showAdd && <AddPropertyForm onCancel={() => setShowAdd(false)} onSave={create} defaultCurrency={state.meta?.currency} />}

      {state.notes.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-xs text-muted-foreground space-y-2" data-testid="properties-country-notes">
          <div className="font-semibold text-amber-500 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Tax & legal notes for your country
          </div>
          {state.notes.map((n, i) => <p key={i} className="leading-relaxed">{n}</p>)}
        </div>
      )}
    </div>
  );
}

// ---------------- Portfolio summary --------------------------------
function PortfolioSummary({ portfolio, currency }) {
  const { format } = useCurrency();
  if (!portfolio || portfolio.count === 0) return null;
  const fmt = (v) => format(v, { currency, compact: v >= 100_000 });
  const cells = [
    { label: "Portfolio value",     value: fmt(portfolio.total_value) },
    { label: "Total property debt", value: fmt(portfolio.total_debt) },
    { label: "Your equity",         value: fmt(portfolio.total_user_equity) },
    { label: "Portfolio LTV",       value: portfolio.portfolio_ltv_pct != null ? `${portfolio.portfolio_ltv_pct}%` : "—" },
    { label: "Monthly housing cost", value: fmt(portfolio.monthly_housing_cost) },
    { label: "Rental cash flow / mo", value: fmt(portfolio.monthly_rental_cash_flow) },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="portfolio-summary">
      {cells.map((c) => (
        <div key={c.label} className="rounded-lg border border-border bg-card/40 px-3 py-2.5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{c.label}</div>
          <div className="mt-1 text-sm font-display font-semibold tabular-nums">{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------- Suggestions --------------------------------------
function SuggestionsPanel({ suggestions, onConfirm, currency }) {
  const { format } = useCurrency();
  const fmt = (v) => format(v, { currency, compact: v >= 100_000 });
  return (
    <div className="rounded-xl border border-blue-500/40 bg-blue-500/5 p-4 space-y-3" data-testid="link-suggestions">
      <div className="flex items-start gap-2">
        <LinkIcon className="w-4 h-4 text-blue-500 mt-0.5" />
        <div className="text-xs">
          <div className="font-semibold text-blue-500">Possible duplicate mortgages found</div>
          <div className="text-muted-foreground mt-0.5">
            We think some of your general Debt Manager entries might be the same mortgage as one attached to a property.
            Click Confirm to link them so the balance appears only once in your liabilities.
          </div>
        </div>
      </div>
      {suggestions.map((s, i) => (
        <div key={i} className="rounded-lg bg-card/60 border border-border p-3 flex items-start gap-3">
          <div className="flex-1 text-xs">
            <div className="font-semibold">{s.debt_name || "Unnamed debt"}</div>
            <div className="text-muted-foreground mt-0.5">
              Debt Manager balance <span className="font-mono">{fmt(s.debt_balance)}</span>
              <span className="mx-2">↔</span>
              <span className="font-semibold">{s.property_name}</span>
              <span className="text-muted-foreground"> mortgage </span>
              <span className="font-mono">{fmt(s.financing_balance)}</span>
              <span className={`ml-2 text-[10px] font-mono uppercase tracking-widest ${s.confidence === "high" ? "text-emerald-500" : "text-amber-500"}`}>
                {s.confidence} confidence
              </span>
            </div>
          </div>
          <Button size="sm" onClick={() => onConfirm(s)} data-testid={`confirm-link-${i}`} className="gap-1.5">
            Confirm link <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}

// ---------------- Empty state --------------------------------------
function EmptyState({ onAdd }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-border p-8 text-center" data-testid="properties-empty">
      <Home className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
      <div className="font-display font-semibold">No properties yet</div>
      <div className="text-xs text-muted-foreground mt-1 mb-4 max-w-md mx-auto">
        Add your home, rental, or other real estate to include equity in your net worth and unlock property-aware projections.
      </div>
      <Button onClick={onAdd} data-testid="properties-add-first" className="gap-1.5">
        <Plus className="w-4 h-4" /> Add your first property
      </Button>
    </div>
  );
}

// ---------------- Property Card ------------------------------------
function PropertyCard({ property, currency, onUpdate, onDelete, onDuplicate }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(property);
  const { format } = useCurrency();
  useEffect(() => setDraft(property), [property]);

  const calc = useMemo(() => summariseProperty(property), [property]);
  const nativeCcy = property.native_currency || currency;
  const fmt = (v) => format(v, { currency: nativeCcy, compact: Math.abs(v) >= 100_000 });

  const saveTop = () => {
    onUpdate({
      name: draft.name, property_type: draft.property_type,
      current_value: Number(draft.current_value) || 0,
      ownership_pct: Number(draft.ownership_pct) || 100,
      is_primary_residence: !!draft.is_primary_residence,
      is_rental: !!draft.is_rental,
      country_code: draft.country_code || null,
      native_currency: draft.native_currency || nativeCcy,
    });
    setEdit(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card/40 overflow-hidden" data-testid={`property-card-${property.id}`}>
      {/* Compact summary row (always visible) */}
      <div className="p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Home className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <div className="font-display font-semibold">{property.name}</div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {property.property_type?.replaceAll("_", " ")}
            </span>
            {property.native_currency && property.native_currency !== currency && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-500 border border-amber-500/40">
                {property.native_currency}
              </span>
            )}
          </div>
          <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
            <SummaryStat label="Value"          value={fmt(calc.current_value)} />
            <SummaryStat label="Mortgage"       value={fmt(calc.total_debt)} />
            <SummaryStat label="Your equity"    value={fmt(calc.user_equity)} accent={calc.user_equity >= 0 ? "positive" : "negative"} />
            <SummaryStat label="LTV"            value={calc.ltv_pct != null ? `${calc.ltv_pct}%` : "—"} />
            {property.is_rental ? (
              <>
                <SummaryStat label="Monthly rent"    value={fmt(calc.monthly_gross_rent)} />
                <SummaryStat label="Cash flow / mo"  value={fmt(calc.monthly_cash_flow)} accent={calc.monthly_cash_flow >= 0 ? "positive" : "negative"} />
                <SummaryStat label="Cap rate"        value={calc.cap_rate_pct != null ? `${calc.cap_rate_pct}%` : "—"} />
                <SummaryStat label="DSCR"            value={calc.dscr != null ? calc.dscr : "—"} />
              </>
            ) : (
              <>
                <SummaryStat label="Monthly cost"    value={fmt(calc.monthly_carrying_cost)} />
                <SummaryStat label="Ownership"       value={`${property.ownership_pct}%`} />
                <SummaryStat label="Country"         value={property.country_code || "—"} />
                <SummaryStat label="Financing rows"  value={(property.financing || []).length} />
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onDuplicate} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Duplicate" data-testid={`property-duplicate-${property.id}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-500/10 text-red-500" title="Archive" data-testid={`property-delete-${property.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setOpen((v) => !v)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title={open ? "Collapse" : "Expand"} data-testid={`property-toggle-${property.id}`}>
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded body */}
      {open && (
        <div className="border-t border-border p-4 space-y-4 bg-background/40" data-testid={`property-detail-${property.id}`}>
          {/* Basic edit form */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Name">
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid={`property-name-${property.id}`} />
            </Field>
            <Field label="Type">
              <Select value={draft.property_type} onChange={(v) => setDraft({ ...draft, property_type: v })} options={PROPERTY_TYPES} testId={`property-type-${property.id}`} />
            </Field>
            <Field label="Country">
              <Select value={draft.country_code || ""} onChange={(v) => setDraft({ ...draft, country_code: v || null })} options={[["", "— select —"], ...COUNTRIES.map((c) => [c.code, `${c.code} — ${c.name}`])]} testId={`property-country-${property.id}`} />
            </Field>
            <Field label="Native currency">
              <Select value={draft.native_currency || nativeCcy} onChange={(v) => setDraft({ ...draft, native_currency: v })} options={CURRENCIES.map((c) => [c, c])} testId={`property-currency-${property.id}`} />
            </Field>
            <Field label="Current value">
              <NumInput value={draft.current_value} onChange={(v) => setDraft({ ...draft, current_value: v })} testId={`property-value-${property.id}`} />
            </Field>
            <Field label="Ownership %">
              <NumInput value={draft.ownership_pct} onChange={(v) => setDraft({ ...draft, ownership_pct: v })} step={1} testId={`property-own-${property.id}`} />
            </Field>
            <Field label="Primary residence">
              <Toggle checked={!!draft.is_primary_residence} onChange={(v) => setDraft({ ...draft, is_primary_residence: v })} testId={`property-primary-${property.id}`} />
            </Field>
            <Field label="Rental property">
              <Toggle checked={!!draft.is_rental} onChange={(v) => setDraft({ ...draft, is_rental: v })} testId={`property-rental-${property.id}`} />
            </Field>
          </div>

          <div className="flex justify-end">
            <Button size="sm" onClick={saveTop} data-testid={`property-save-${property.id}`}>Save changes</Button>
          </div>

          {/* Financing rows */}
          <FinancingEditor
            propertyId={property.id}
            financing={property.financing || []}
            currency={nativeCcy}
            onSave={(financing) => onUpdate({ financing })}
          />

          {/* Expenses */}
          <ExpensesEditor
            propertyId={property.id}
            expenses={property.expenses || []}
            currency={nativeCcy}
            onSave={(expenses) => onUpdate({ expenses })}
          />

          {/* Rental */}
          {property.is_rental && (
            <RentalEditor
              propertyId={property.id}
              rental={property.rental || null}
              currency={nativeCcy}
              onSave={(rental) => onUpdate({ rental })}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- Financing editor --------------------------------
function FinancingEditor({ propertyId, financing, currency, onSave }) {
  const [rows, setRows] = useState(financing);
  useEffect(() => setRows(financing), [financing]);
  const add = () => setRows([...rows, {
    id: crypto.randomUUID(), lender: "", financing_type: "mortgage", current_balance: 0,
    interest_rate_pct: 6.0, rate_type: "fixed", payment_amount: 0, payment_frequency: "monthly",
    amortization_years: 25, currency,
  }]);
  const upd = (i, patch) => setRows(rows.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  const rm = (i) => setRows(rows.filter((_, j) => j !== i));
  return (
    <div className="border border-border rounded-lg p-3" data-testid={`financing-editor-${propertyId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Financing / Mortgages
        </div>
        <button onClick={add} className="text-xs text-primary hover:underline" data-testid={`financing-add-${propertyId}`}>+ Add financing</button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">No debt on this property.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((f, i) => (
            <div key={f.id || i} className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-border rounded-md p-2 relative">
              {f.linked_debt_id && (
                <div className="absolute -top-2 -right-2 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-500 border border-blue-500/40 flex items-center gap-1">
                  <LinkIcon className="w-2.5 h-2.5" /> linked to Debt Manager
                </div>
              )}
              <Field label="Lender">
                <Input value={f.lender || ""} onChange={(e) => upd(i, { lender: e.target.value })} data-testid={`fin-lender-${i}`} />
              </Field>
              <Field label="Type">
                <Select value={f.financing_type} onChange={(v) => upd(i, { financing_type: v })} options={FINANCING_TYPES} testId={`fin-type-${i}`} />
              </Field>
              <Field label="Balance">
                <NumInput value={f.current_balance} onChange={(v) => upd(i, { current_balance: v })} testId={`fin-balance-${i}`} />
              </Field>
              <Field label="Interest %">
                <NumInput value={f.interest_rate_pct} onChange={(v) => upd(i, { interest_rate_pct: v })} step={0.01} testId={`fin-rate-${i}`} />
              </Field>
              <Field label="Payment">
                <NumInput value={f.payment_amount || 0} onChange={(v) => upd(i, { payment_amount: v })} testId={`fin-pay-${i}`} />
              </Field>
              <Field label="Frequency">
                <Select value={f.payment_frequency || "monthly"} onChange={(v) => upd(i, { payment_frequency: v })} options={PAYMENT_FREQ} testId={`fin-freq-${i}`} />
              </Field>
              <Field label="Amortization (yrs)">
                <NumInput value={f.amortization_years || 25} onChange={(v) => upd(i, { amortization_years: v })} step={1} testId={`fin-amort-${i}`} />
              </Field>
              <Field label="Remaining (yrs)">
                <NumInput value={f.remaining_amortization_years || f.amortization_years || 25} onChange={(v) => upd(i, { remaining_amortization_years: v })} step={1} testId={`fin-rem-${i}`} />
              </Field>
              <div className="col-span-2 md:col-span-4 flex justify-end">
                <button onClick={() => rm(i)} className="text-xs text-red-500 hover:underline" data-testid={`fin-remove-${i}`}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end mt-2">
        <Button size="sm" variant="outline" onClick={() => onSave(rows)} data-testid={`financing-save-${propertyId}`}>Save financing</Button>
      </div>
    </div>
  );
}

// ---------------- Expenses editor ---------------------------------
function ExpensesEditor({ propertyId, expenses, currency, onSave }) {
  const [rows, setRows] = useState(expenses);
  useEffect(() => setRows(expenses), [expenses]);
  const add = () => setRows([...rows, { id: crypto.randomUUID(), kind: "property_tax", amount: 0, frequency: "monthly", currency }]);
  const upd = (i, patch) => setRows(rows.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  const rm = (i) => setRows(rows.filter((_, j) => j !== i));
  return (
    <div className="border border-border rounded-lg p-3" data-testid={`expenses-editor-${propertyId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recurring expenses</div>
        <button onClick={add} className="text-xs text-primary hover:underline" data-testid={`expense-add-${propertyId}`}>+ Add expense</button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">No recurring expenses recorded.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((e, i) => (
            <div key={e.id || i} className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-border rounded-md p-2">
              <Field label="Kind">
                <Select value={e.kind} onChange={(v) => upd(i, { kind: v })} options={EXPENSE_KINDS} testId={`exp-kind-${i}`} />
              </Field>
              <Field label="Amount">
                <NumInput value={e.amount} onChange={(v) => upd(i, { amount: v })} testId={`exp-amount-${i}`} />
              </Field>
              <Field label="Frequency">
                <Select value={e.frequency || "monthly"} onChange={(v) => upd(i, { frequency: v })} options={[["monthly","Monthly"],["quarterly","Quarterly"],["annual","Annual"]]} testId={`exp-freq-${i}`} />
              </Field>
              <div className="flex items-end">
                <button onClick={() => rm(i)} className="text-xs text-red-500 hover:underline" data-testid={`exp-remove-${i}`}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end mt-2">
        <Button size="sm" variant="outline" onClick={() => onSave(rows)} data-testid={`expenses-save-${propertyId}`}>Save expenses</Button>
      </div>
    </div>
  );
}

// ---------------- Rental editor -----------------------------------
function RentalEditor({ propertyId, rental, currency, onSave }) {
  const empty = {
    monthly_gross_rent: 0, other_income_annual: 0, vacancy_rate_pct: 5, property_management_pct: 0,
    property_management_flat_annual: 0, annual_maintenance_estimate: 0, annual_repairs_estimate: 0,
    tenant_pays_utilities: true, owner_paid_utilities_annual: 0, estimated_appreciation_pct: 3,
  };
  const [r, setR] = useState({ ...empty, ...(rental || {}) });
  useEffect(() => setR({ ...empty, ...(rental || {}) }), [rental]);
  const s = (k, v) => setR({ ...r, [k]: v });
  return (
    <div className="border border-border rounded-lg p-3" data-testid={`rental-editor-${propertyId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Rental details</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Field label="Monthly gross rent"><NumInput value={r.monthly_gross_rent} onChange={(v) => s("monthly_gross_rent", v)} testId="rental-rent" /></Field>
        <Field label="Other income / yr"><NumInput value={r.other_income_annual} onChange={(v) => s("other_income_annual", v)} testId="rental-other" /></Field>
        <Field label="Vacancy rate %"><NumInput value={r.vacancy_rate_pct} onChange={(v) => s("vacancy_rate_pct", v)} step={0.5} testId="rental-vacancy" /></Field>
        <Field label="Mgmt %"><NumInput value={r.property_management_pct} onChange={(v) => s("property_management_pct", v)} step={0.5} testId="rental-mgmt-pct" /></Field>
        <Field label="Mgmt flat / yr"><NumInput value={r.property_management_flat_annual} onChange={(v) => s("property_management_flat_annual", v)} testId="rental-mgmt-flat" /></Field>
        <Field label="Maint est / yr"><NumInput value={r.annual_maintenance_estimate} onChange={(v) => s("annual_maintenance_estimate", v)} testId="rental-maint" /></Field>
        <Field label="Repairs est / yr"><NumInput value={r.annual_repairs_estimate} onChange={(v) => s("annual_repairs_estimate", v)} testId="rental-repairs" /></Field>
        <Field label="Appreciation %"><NumInput value={r.estimated_appreciation_pct} onChange={(v) => s("estimated_appreciation_pct", v)} step={0.5} testId="rental-appreciation" /></Field>
        <Field label="Tenant pays utilities"><Toggle checked={!!r.tenant_pays_utilities} onChange={(v) => s("tenant_pays_utilities", v)} testId="rental-tenantpay" /></Field>
        {!r.tenant_pays_utilities && <Field label="Owner utils / yr"><NumInput value={r.owner_paid_utilities_annual} onChange={(v) => s("owner_paid_utilities_annual", v)} testId="rental-ownerutil" /></Field>}
      </div>
      <div className="flex justify-end mt-2">
        <Button size="sm" variant="outline" onClick={() => onSave(r)} data-testid={`rental-save-${propertyId}`}>Save rental info</Button>
      </div>
    </div>
  );
}

// ---------------- Add property form -------------------------------
function AddPropertyForm({ onCancel, onSave, defaultCurrency }) {
  const [d, setD] = useState({
    name: "", property_type: "primary_residence",
    country_code: "", region: "", city: "",
    purchase_price: 0, current_value: 0, ownership_pct: 100,
    is_primary_residence: true, is_rental: false,
    native_currency: defaultCurrency || "USD",
  });
  const s = (k, v) => setD({ ...d, [k]: v });
  const submit = () => {
    if (!d.name.trim()) { toast.error("Give the property a name"); return; }
    onSave({
      name: d.name,
      property_type: d.property_type,
      country_code: d.country_code || null,
      region: d.region || null,
      city: d.city || null,
      purchase_price: Number(d.purchase_price) || null,
      current_value: Number(d.current_value) || 0,
      ownership_pct: Number(d.ownership_pct) || 100,
      is_primary_residence: !!d.is_primary_residence,
      is_rental: !!d.is_rental,
      native_currency: d.native_currency,
    });
  };
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="properties-add-form">
      <div className="font-display font-semibold">Add property</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Property nickname"><Input placeholder="Downtown Toronto Condo" value={d.name} onChange={(e) => s("name", e.target.value)} data-testid="new-property-name" /></Field>
        <Field label="Type"><Select value={d.property_type} onChange={(v) => s("property_type", v)} options={PROPERTY_TYPES} testId="new-property-type" /></Field>
        <Field label="Country"><Select value={d.country_code} onChange={(v) => s("country_code", v)} options={[["", "— select —"], ...COUNTRIES.map((c) => [c.code, c.name])]} testId="new-property-country" /></Field>
        <Field label="Native currency"><Select value={d.native_currency} onChange={(v) => s("native_currency", v)} options={CURRENCIES.map((c) => [c, c])} testId="new-property-currency" /></Field>
        <Field label="City"><Input value={d.city} onChange={(e) => s("city", e.target.value)} data-testid="new-property-city" /></Field>
        <Field label="Region / Province"><Input value={d.region} onChange={(e) => s("region", e.target.value)} data-testid="new-property-region" /></Field>
        <Field label="Purchase price"><NumInput value={d.purchase_price} onChange={(v) => s("purchase_price", v)} testId="new-property-price" /></Field>
        <Field label="Current value"><NumInput value={d.current_value} onChange={(v) => s("current_value", v)} testId="new-property-value" /></Field>
        <Field label="Ownership %"><NumInput value={d.ownership_pct} onChange={(v) => s("ownership_pct", v)} step={1} testId="new-property-own" /></Field>
        <Field label="Primary residence"><Toggle checked={d.is_primary_residence} onChange={(v) => s("is_primary_residence", v)} testId="new-property-primary" /></Field>
        <Field label="Rental property"><Toggle checked={d.is_rental} onChange={(v) => s("is_rental", v)} testId="new-property-rental" /></Field>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} data-testid="new-property-cancel">Cancel</Button>
        <Button onClick={submit} data-testid="new-property-save">Add property</Button>
      </div>
    </div>
  );
}

// ---------------- Reusable field bits -----------------------------
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function NumInput({ value, onChange, step = 100, testId }) {
  return (
    <Input type="number" inputMode="decimal" value={value ?? 0} step={step} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} className="font-mono" />
  );
}

function Select({ value, onChange, options, testId }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full bg-background border border-border rounded-md px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none">
      {options.map(([v, l]) => <option key={v || "_none"} value={v}>{l}</option>)}
    </select>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`w-11 h-6 rounded-full transition-colors relative ${checked ? "bg-primary" : "bg-muted"}`}
      data-testid={testId} aria-pressed={checked}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function SummaryStat({ label, value, accent }) {
  const tint = accent === "positive" ? "text-emerald-500" : accent === "negative" ? "text-red-500" : "";
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground truncate">{label}</div>
      <div className={`font-mono font-semibold text-xs tabular-nums truncate ${tint}`}>{value}</div>
    </div>
  );
}

// ---------------- Local-only preview calculation ------------------
// Mirrors backend property_service.compute_property for the summary line
// so the UI doesn't need a per-property round-trip on every field change.
function summariseProperty(p) {
  const cv = Number(p.current_value || 0);
  const own = Number(p.ownership_pct || 100) / 100;
  const total_debt = (p.financing || []).reduce((s, f) => s + Number(f.current_balance || 0), 0);
  const equity = cv - total_debt;
  const user_equity = equity * own;
  const ltv_pct = cv > 0 ? Math.round((total_debt / cv) * 1000) / 10 : null;
  const annual_expenses = (p.expenses || []).reduce((s, e) => {
    const mult = e.frequency === "annual" ? 1 : e.frequency === "quarterly" ? 4 : 12;
    return s + Number(e.amount || 0) * mult;
  }, 0);
  // rough debt-service estimate (interest-only proxy)
  const annual_ds = (p.financing || []).reduce((s, f) => {
    const b = Number(f.current_balance || 0);
    const r = Number(f.interest_rate_pct || 0) / 100;
    const y = Number(f.remaining_amortization_years || f.amortization_years || 25);
    if (b <= 0 || y <= 0) return s;
    const n = y * 12; const rm = r / 12;
    const payment = rm === 0 ? b / n : b * rm * Math.pow(1 + rm, n) / (Math.pow(1 + rm, n) - 1);
    return s + payment * 12;
  }, 0);
  const monthly_carrying_cost = (annual_expenses + annual_ds) / 12;
  let monthly_gross_rent = 0, monthly_cash_flow = 0, cap_rate_pct = null, dscr = null;
  if (p.is_rental && p.rental) {
    monthly_gross_rent = Number(p.rental.monthly_gross_rent || 0);
    const annual_gross = monthly_gross_rent * 12 + Number(p.rental.other_income_annual || 0);
    const egi = annual_gross * (1 - Number(p.rental.vacancy_rate_pct || 0) / 100);
    const opex = annual_expenses
      + Number(p.rental.annual_maintenance_estimate || 0)
      + Number(p.rental.annual_repairs_estimate || 0)
      + (p.rental.tenant_pays_utilities ? 0 : Number(p.rental.owner_paid_utilities_annual || 0))
      + Number(p.rental.property_management_flat_annual || 0)
      + annual_gross * (Number(p.rental.property_management_pct || 0) / 100);
    const noi = egi - opex;
    monthly_cash_flow = (noi - annual_ds) / 12;
    cap_rate_pct = cv > 0 ? Math.round((noi / cv) * 1000) / 10 : null;
    dscr = annual_ds > 0 ? Math.round((noi / annual_ds) * 100) / 100 : null;
  }
  return {
    current_value: cv, total_debt, user_equity, ltv_pct,
    monthly_carrying_cost, monthly_gross_rent, monthly_cash_flow,
    cap_rate_pct, dscr,
  };
}
